// JOBE-0164.3: FailureClassIndex — the load-bearing W6 surface.
//
// Each row carries:
//   - rank · sub-label · title · description · agents-affected chips ·
//     run count · "Fix cohort" toggle.
// The currency-parsing row gets the .signal-row tint (case-insensitive
// substring match against the title — robust to fixture edits).
// Agent chips drill to W5 with withDrillContext({source:'fleet-failure-class', …}).
// "Fix cohort" toggles an inline expansion mounting <CohortFixPreview/>.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PillChip } from '../components/PillChip/PillChip.jsx';
import { withDrillContext } from '../lib/drillContext.js';
import { CohortFixPreview } from './CohortFixPreview.jsx';
import './FailureClassIndex.css';

export function slugify(s) {
  if (!s) return '';
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')   // collapse any run of non-alnum to single '-'
    .replace(/^-+|-+$/g, '');      // strip leading/trailing dashes
}

function titleCase(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function isSignalClass(cls) {
  if (!cls) return false;
  return cls.toLowerCase().includes('currency');
}

/**
 * @param {object} props
 * @param {object} props.fleet  loaded `promptops.mock.fleet` fixture
 */
export function FailureClassIndex({ fleet }) {
  const navigate = useNavigate();
  const [openClassId, setOpenClassId] = useState(null);

  const rows = fleet?.failureClasses ?? [];

  return (
    <section id="failure-class-index" className="failure-class-index" aria-label="Failure classes">
      <header className="failure-class-index__head">
        <h2 className="failure-class-index__title">Failure classes</h2>
        <span className="failure-class-index__caption">
          drill an agent chip to W5 · fix the cohort with one bundle
        </span>
      </header>
      <ul className="failure-class-index__list" role="list">
        {rows.map((cls, i) => {
          const id = cls.id || slugify(cls.cls);
          const isSignal = isSignalClass(cls.cls);
          const isOpen = openClassId === id;
          return (
            <li key={id} className="failure-class-index__row-wrap">
              <div
                className={`failure-class-index__row${isSignal ? ' signal-row' : ''}${isOpen ? ' is-open' : ''}`}
                data-failure-class-id={id}
              >
                <div className="fc-col fc-col--rank">{i + 1}</div>
                <div className="fc-col fc-col--cls">
                  <div className="fc-cls-sublabel">failure class</div>
                  <div className="fc-cls-title">{cls.cls}</div>
                </div>
                <div className="fc-col fc-col--desc">{cls.description || ''}</div>
                <div className="fc-col fc-col--agents">
                  {(cls.agentsAffected || []).map((name) => (
                    <PillChip
                      key={name}
                      label={titleCase(name)}
                      onClick={() =>
                        navigate(
                          withDrillContext(
                            `/promptops/agent/${name}?failureClass=${id}`,
                            { source: 'fleet-failure-class', classId: id, agentName: name }
                          )
                        )
                      }
                    />
                  ))}
                </div>
                <div className="fc-col fc-col--count">{cls.count ?? 0}</div>
                <div className="fc-col fc-col--cta">
                  <button
                    type="button"
                    className="fc-fix-cohort-btn"
                    onClick={() => setOpenClassId(isOpen ? null : id)}
                  >
                    {isOpen ? 'CLOSE ↓' : 'FIX COHORT →'}
                  </button>
                </div>
              </div>
              {isOpen && (
                <CohortFixPreview
                  classId={id}
                  className={cls.cls}
                  agentsAffected={cls.agentsAffected || []}
                  proposedFix={cls.proposedFix || 'Pre-normalise inputs before downstream use.'}
                  onClose={() => setOpenClassId(null)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default FailureClassIndex;
